import { useQuery } from "@tanstack/react-query";
import { SuperAdminLayout } from "@/components/admin/SuperAdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ReferralTable } from "@/components/referrals/ReferralTable";
import { getAdminRewardColumns, type RewardData } from "@/components/referrals/tableConfigs";
import { RewardsCard } from "@/components/dashboard/RewardsCard";
import { RewardsCardSkeleton } from "@/components/rewards/RewardsCardSkeleton";
import { RewardsTableSkeleton } from "@/components/rewards/RewardsTableSkeleton";
import { getAllRewards, getAdminStats } from "@/api/stats";
import { RewardStatus } from "@/lib/schema";
import { Users } from "lucide-react";

// Import custom SVG icons
import iconRewards from "@/assets/rewards/icon_rewards.svg";
import iconPaid from "@/assets/rewards/icon_paid.svg";
import iconPending from "@/assets/rewards/icon_pending.svg";

export default function RewardsHub() {
  const { data: rewards = [], isLoading: rewardsLoading } = useQuery<RewardData[]>({
    queryKey: ["admin-rewards"],
    queryFn: getAllRewards,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const { data: adminStats, isLoading: statsLoading } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: getAdminStats,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  // Calculate totals from rewards data
  const totalRewardsDistributed = rewards.reduce((sum, reward) => {
    return sum + parseInt(reward.amount.replace(/[$,]/g, ''));
  }, 0);

  const paidRewards = rewards
    .filter(reward => reward.status === RewardStatus.PAID)
    .reduce((sum, reward) => {
      return sum + parseInt(reward.amount.replace(/[$,]/g, ''));
    }, 0);

  const pendingRewards = rewards
    .filter(reward => reward.status === RewardStatus.PENDING)
    .reduce((sum, reward) => {
      return sum + parseInt(reward.amount.replace(/[$,]/g, ''));
    }, 0);

  // Get unique users with rewards
  const uniqueRewardRecipients = new Set(rewards.map(reward => reward.referrerId)).size;

  return (
    <SuperAdminLayout 
      title="Rewards Hub"
      subtitle="Monitor all reward distributions, track payouts, and manage reward programs across the platform."
    >
      {/* Admin Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statsLoading || rewardsLoading ? (
          <>
            <RewardsCardSkeleton />
            <RewardsCardSkeleton />
            <RewardsCardSkeleton />
            <RewardsCardSkeleton />
          </>
        ) : (
          <>
            <RewardsCard
              title="TOTAL DISTRIBUTED"
              value={totalRewardsDistributed}
              icon={iconRewards}
              variant="dark"
              testId="total-distributed-card"
            />
            
            <RewardsCard
              title="PAID OUT"
              value={paidRewards}
              icon={iconPaid}
              variant="default"
              testId="paid-rewards-card"
            />
            
            <RewardsCard
              title="PENDING PAYOUT"
              value={pendingRewards}
              icon={iconPending}
              variant="default"
              testId="pending-rewards-card"
            />

            <RewardsCard
              title="REWARD RECIPIENTS"
              value={uniqueRewardRecipients.toString()}
              icon={iconRewards}
              variant="default"
              testId="recipients-card"
            />
          </>
        )}
      </div>

      {/* Rewards Table */}
      <Card>
        <CardHeader>
          <CardTitle className="space-grotesk">All Rewards</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {rewardsLoading ? (
            <RewardsTableSkeleton rows={10} />
          ) : (
            <ReferralTable
              data={rewards}
              columns={getAdminRewardColumns()}
              emptyMessage="No rewards found"
            />
          )}
        </CardContent>
      </Card>
    </SuperAdminLayout>
  );
}
